import React, { useState, useCallback, useEffect } from "react";
import { Row, Col, Button, Table } from "react-bootstrap";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery, useMutation, useLazyQuery } from "@apollo/react-hooks";
import displayErrorToast from "../../components/utils/displayErrorToast";
import PaginationComponent from "../../components/PaginationComponent";
import SubHeaderComponent from "../../components/SubHeaderComponent";
import EditRow from "./EditRow";
import DisplayRow from "./DisplayRow";
import {
  LOAD_DEPARTMENT_TYPES_QUERY,
  CREATE_DEPARTMENT_TYPE_MUTATION,
  UPDATE_DEPARTMENT_TYPE_MUTATION,
  DELETE_DEPARTMENT_TYPE_MUTATION,
  GET_DEPARTMENT_TYPES_COUNT
} from "../../services/DepartmentTypeService";
import * as DefaultLabels from '../../assets/glossary.json';
import LabelComponent from "../../components/utils/getCompanyLabel";
const labels = DefaultLabels.default;

const levelTypes = [
  {
    value: "LVL1",
    text: "Level 1"
  },
  {
    value: "LVL2",
    text: "Level 2"
  },
  {
    value: "LVL3",
    text: "Level 3"
  },
  {
    value: "LVL4",
    text: "Level 4"
  },
  {
    value: "DEP",
    text: "Department"
  }
]

const DepartmentTypesScreen = ({ togglePageLoad, user, ...props}) => {
  const [departmentTypes, setDepartmentTypes] = useState([]);
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false);
  const [itemBeingEdited, setItemBeingEdited] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [firstIndex, setFirstIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(9);

  const getLabelText = key => <LabelComponent val={key}/>

  const reset = () => {
    setIsCreatingNewItem(false);
    setItemBeingEdited("");
    props.toggleDirtyState()
  };

  const onError = (error) => {
    displayErrorToast(error);
    reset();
  };

  const getText = val => {
    const companyId = localStorage.getItem("companyId");
    let lang = "EN";
    let langLabels = labels.find(lbl => lbl.lang === lang);
    let def = langLabels.glossary.find(label => label.key === val);

    if(user && user.companies && user.companies.length) {
      const company = user.companies.find(company => company.id === companyId);
      if(company) {
        lang = company.language;
        langLabels = labels.find(lbl => lbl.lang === lang);
        def = langLabels.glossary.find(label => label.key === val);
        if(!def) {
          lang = "EN";
          langLabels = labels.find(lbl => lbl.lang === lang);
          def = langLabels.glossary.find(label => label.key === val);
        }
      }
      if(company && company.companyGlossaries) {
        const custom = company.companyGlossaries.find(label => label.key === val);

        if(custom) return custom.value
      }
    }

    if(!def) {
      console.log(val)
    }
    return def.value
  }

  useEffect(() => {
    togglePageLoad(true)
  }, [togglePageLoad])

  const onLoaded = useCallback(
    (data) => {
      setDepartmentTypes(data.departmentTypes)
      togglePageLoad(false)
    },
    [setDepartmentTypes, togglePageLoad]
  );

  const setCount = useCallback(
    (data) => setTotalPages(Math.ceil(data.departmentTypesCount / 10)),
    [setTotalPages]
  )

  useQuery(LOAD_DEPARTMENT_TYPES_QUERY, {
    onCompleted: onLoaded,
  });

  useQuery(GET_DEPARTMENT_TYPES_COUNT, {
    onCompleted: setCount
  })

  const [getCount] = useLazyQuery(GET_DEPARTMENT_TYPES_COUNT, {
    onCompleted: setCount
  })

  const [createDepartmentType] = useMutation(CREATE_DEPARTMENT_TYPE_MUTATION, {
    onError,
    onCompleted: ({ createDepartmentType }) => {
      setDepartmentTypes([createDepartmentType, ...departmentTypes]);
      getCount()
      reset();
    },
  });

  const [updateDepartmentType] = useMutation(UPDATE_DEPARTMENT_TYPE_MUTATION, {
    onError,
    onCompleted: ({ updateDepartmentType }) => {
      const nextDepartmentTypes = departmentTypes.map((e) =>
        e.id === updateDepartmentType.id ? updateDepartmentType : e
      );
      setDepartmentTypes(nextDepartmentTypes);
      reset();
    },
  });

  const [deleteDepartmentType] = useMutation(DELETE_DEPARTMENT_TYPE_MUTATION, {
    onError,
    onCompleted: ({ deleteDepartmentType }) => {
      setDepartmentTypes(
        departmentTypes.filter((e) => e.id !== deleteDepartmentType.id)
      );
      getCount();
      reset();
    },
  });

  const onOpenCreate = () => {
    setIsCreatingNewItem(true);
    props.toggleDirtyState()
    setItemBeingEdited("");
  };

  const onOpenEdit = (id) => {
    setIsCreatingNewItem(false);
    props.toggleDirtyState()
    setItemBeingEdited(id);
  };

  const onDelete = (id) => {
    deleteDepartmentType({ variables: { id } });
  };

  const onSave = ({ id, name, level }) => {
    if (id) return updateDepartmentType({ variables: { id, name, level } });
    createDepartmentType({ variables: { name, level } });
  };

  function setPagination (page) {
    setFirstIndex((parseInt(page) * 10) - 10)
    setLastIndex((parseInt(page) * 10) - 1)
    setPage(page)
  }

  return (
    <>
      <SubHeaderComponent>
        <Row className="justify-content-center">
          <Col className="text-right" xs={12}>
            <Button onClick={onOpenCreate} className={"btn-nrml" + (isCreatingNewItem || itemBeingEdited !== "" ? " disabled" : "")}>
              {getLabelText("add")}
              <FontAwesomeIcon className="ml-2" icon={faPlus} />
            </Button>
          </Col>
        </Row>
      </SubHeaderComponent>
      <div className="grey-header-space container-left">
        <Row className="justify-content-center paddingType">
          <Col xl={12}>
            <form>
              <Table striped bordered hover className="table-heading-background user-text">
                <thead>
                <tr>
                  <th width="70%">{getLabelText("team_type_description")}</th>
                  <th width="15%">{getLabelText("type_level")}</th>
                  <th width="15%"></th>
                </tr>
                </thead>
                {departmentTypes && (
                    <tbody>
                    {isCreatingNewItem ? (
                        <EditRow onSave={onSave} onCancel={reset} levelTypes={levelTypes}/>
                    ) : null}
                    {departmentTypes.map(({ name, id, hasDepartments, level }, i) =>
                        i >= firstIndex && i <= lastIndex ? (
                            id === itemBeingEdited ? (
                                <EditRow
                                    key={id}
                                    name={name}
                                    id={id}
                                    onSave={onSave}
                                    onCancel={reset}
                                    levelTypes={levelTypes}
                                    level={level}
                                />
                            ) : (
                                <DisplayRow
                                    onOpenEdit={onOpenEdit}
                                    onDelete={onDelete}
                                    getText={getText}
                                    key={id}
                                    name={name}
                                    id={id}
                                    disabled={isCreatingNewItem || itemBeingEdited !== ""}
                                    hasDepartments={hasDepartments}
                                    levelTypes={levelTypes}
                                    level={level}
                                />
                            )
                        ) : null
                    )}
                    </tbody>
                )}
              </Table>
              {totalPages > 1
                  ? (
                      <PaginationComponent
                          active={page}
                          disabled={false}
                          setPage={setPagination}
                          total={totalPages}
                      />
                  ) : null
              }
            </form>
          </Col>
        </Row>
      </div>
    </>
  );
};
const mapStateToProps = (state) => ({
  user: state.userReducer.user
})

const mapDispatchToProps = {

};

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentTypesScreen);
